export interface TableDeepLinkState {
    /** Simple searchtext (already processed for backend, if caller uses that). */
    search?: string | null;
    /** Dialect for simple searchtext. When omitted, caller decides default. */
    sd?: 'expression' | 'freetext' | null;
    /** When true, allow combining simple + advanced. */
    combine?: boolean | null;
    /** Search In attribute ids (comma-separated). */
    si?: string[] | null;
    /** Advanced filter string. */
    filter?: string | null;
    /** 1-based page number. */
    page?: number | null;
}

export function readTableDeepLinkStateFromHash(): TableDeepLinkState {
    if (typeof window === 'undefined') return {};
    const raw = window.location.hash || '';
    const idx = raw.indexOf('?');
    if (idx < 0) return {};
    const qs = raw.slice(idx + 1);
    const params = new URLSearchParams(qs);
    const search = params.get('search');
    const sdRaw = params.get('sd');
    const combineRaw = params.get('combine');
    const siRaw = params.get('si');
    const filter = params.get('filter');
    const rawPage = params.get('page');
    let page: number | null = null;
    if (rawPage) {
        const n = Number(rawPage);
        if (Number.isFinite(n) && n >= 1) page = Math.floor(n);
    }
    return {
        search: search && search.trim() ? search : null,
        sd: sdRaw === 'expression' || sdRaw === 'freetext' ? sdRaw : null,
        combine: combineRaw === '1' || combineRaw === 'true' ? true : null,
        si: siRaw && siRaw.trim() ? siRaw.split(',').map((s) => s.trim()).filter(Boolean) : null,
        filter: filter && filter.trim() ? filter : null,
        page,
    };
}

type HistoryMode = 'replace' | 'push';

/** Update hash via history API without dispatching hashchange manually. */
function setHashNoEvent(hash: string, mode: HistoryMode): void {
    const fragment = (hash ?? '').trim().startsWith('#') ? hash.trim() : `#${(hash ?? '').trim()}`;
    const url = new URL(window.location.href);
    url.hash = fragment;
    if (mode === 'push') window.history.pushState(null, '', url.toString());
    else window.history.replaceState(null, '', url.toString());
}

function unescapeCommasForParam(qs: string, key: string): string {
    if (!qs || !key) return qs;
    // Only unescape commas for the specific param value to keep other encoding intact.
    // Example: si=Id%2CUserPrincipalName -> si=Id,UserPrincipalName
    const pattern = new RegExp(`(^|&)${key}=([^&]*)`, 'g');
    return qs.replace(pattern, (_m, prefix, value) => {
        const nextValue = String(value).replace(/%2C/gi, ',');
        return `${prefix}${key}=${nextValue}`;
    });
}

/**
 * Replace (not push) URL hash query params for deep-linkable table state.
 * Keeps existing non-table query params unless overwritten by caller.
 */
export function writeTableDeepLinkStateToHash(next: TableDeepLinkState, options?: { mode?: HistoryMode }): void {
    if (typeof window === 'undefined') return;
    const raw = window.location.hash || '';
    const [base, qs] = raw.split('?');
    const params = new URLSearchParams(qs ?? '');

    const search = (next.search ?? '').toString().trim();
    const sd = (next.sd ?? null);
    const combine = next.combine === true;
    const si = Array.isArray(next.si) ? next.si.map((s) => String(s).trim()).filter(Boolean) : null;
    const filter = (next.filter ?? '').toString().trim();
    const page = next.page ?? 1;

    if (search) params.set('search', search);
    else params.delete('search');

    // Only keep sd when search is present.
    if (search && (sd === 'expression' || sd === 'freetext')) params.set('sd', sd);
    else params.delete('sd');

    // Only keep combine/si when search is present.
    if (search && combine) params.set('combine', '1');
    else params.delete('combine');

    if (search && si && si.length > 0) params.set('si', si.join(','));
    else params.delete('si');

    if (filter) params.set('filter', filter);
    else params.delete('filter');

    if (page && page > 1) params.set('page', String(page));
    else params.delete('page');

    let out = params.toString();
    // Keep comma-separated lists and common inputs readable in the hash.
    out = unescapeCommasForParam(out, 'si');
    out = unescapeCommasForParam(out, 'search');
    out = unescapeCommasForParam(out, 'filter');
    setHashNoEvent(out ? `${base}?${out}` : base, options?.mode ?? 'replace');
}

